/**
 * Encrypt a credential's secret in the browser, once per gateway that will hold
 * it, so the SaaS only ever relays ciphertext (docs/ClientSealedCredentials.md).
 *
 * The construction is the one gateways already use to replicate credentials to
 * each other, byte for byte. The browser stands in for the "source gateway":
 *
 *   shared = X25519(fresh browser secret, gateway's replication public key)
 *   key    = HKDF-SHA256(salt = none, ikm = shared, info = "netman-cred-repl-v1", 32)
 *   copy   = nonce(24) ‖ XChaCha20-Poly1305(key, nonce, aad, material JSON)
 *   aad    = "netman-cred-repl-v1:{credential}:{reservation}:{gateway}:{proto type}"
 *
 * The reservation id fills the AAD's source-gateway slot. Gateway side:
 * `gateway/src/db/crypto.rs` (`replication_envelope_key`, `replication_aad`,
 * `open_with_secret`) and `handle_install_replicated_credential`.
 *
 * Plain functions with no React, axios or DOM: the e2e suite carries a copy of
 * this file, and both copies are held to the same known-answer vector.
 */
import { xchacha20poly1305 } from '@noble/ciphers/chacha.js';
import { randomBytes } from '@noble/ciphers/utils.js';
import { x25519 } from '@noble/curves/ed25519.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';

/** KDF `info` and AAD prefix: the gateway's `REPL_KEK_INFO` / `REPL_AAD_PREFIX`. */
export const REPLICATION_LABEL = 'netman-cred-repl-v1';

const KEY_LEN = 32;
const NONCE_LEN = 24;

/** A credential as the API names it, carrying a secret the browser can seal. */
export type SealableCredential =
  | { type: 'usernamePassword'; username: string; password: string }
  | { type: 'sshKey'; key: string }
  | { type: 'snmpV2Community'; community: string }
  | {
      type: 'snmpV3';
      username: string;
      authProtocol?: string | null;
      authKey?: string | null;
      privProtocol?: string | null;
      privKey?: string | null;
    }
  | { type: 'apiKey'; key: string }
  | { type: 'preSharedKey'; key: string };

/** The proto `CredentialType` each sealable type travels as (gateway-api.proto). */
export const PROTO_CREDENTIAL_TYPE: Record<SealableCredential['type'], number> = {
  usernamePassword: 1,
  sshKey: 2,
  snmpV2Community: 3,
  snmpV3: 4,
  apiKey: 6,
  preSharedKey: 9,
};

/**
 * The material JSON the gateway decodes for this type
 * (`CredentialMaterial::decode`, `gateway/src/models/credential.rs`). Field
 * names are the gateway's, not the API's: an SNMPv2 community is `name`, an
 * API key is `api_key`. An absent SNMPv3 field is `null`, as the API's
 * `Option<String>` would have carried it.
 */
export function materialJson(credential: SealableCredential): string {
  switch (credential.type) {
    case 'usernamePassword':
      return JSON.stringify({ username: credential.username, password: credential.password });
    case 'sshKey':
      return JSON.stringify({ key: credential.key });
    case 'snmpV2Community':
      return JSON.stringify({ name: credential.community });
    case 'snmpV3':
      return JSON.stringify({
        username: credential.username,
        authProtocol: credential.authProtocol ?? null,
        authKey: credential.authKey ?? null,
        privProtocol: credential.privProtocol ?? null,
        privKey: credential.privKey ?? null,
      });
    case 'apiKey':
      return JSON.stringify({ api_key: credential.key });
    case 'preSharedKey':
      return JSON.stringify({ key: credential.key });
  }
}

/** One gateway the SaaS reserved, with the key to seal to (base64, 32 bytes). */
export interface SealTarget {
  gatewayId: string;
  recipientPublicKey: string;
}

/** What the reserve step returned that the seal binds. */
export interface SealReservation {
  reservationId: string;
  credentialId: string;
  /** The proto `CredentialType` value, as the gateway renders it into the AAD. */
  credType: number;
  targets: SealTarget[];
}

/** One gateway's encrypted copy, ready to commit (all bytes base64). */
export interface SealedCopy {
  gatewayId: string;
  sourcePublicKey: string;
  ciphertext: string;
}

/** The AAD the gateway rebuilds; UUIDs lowercase, as it re-renders them. */
export function replicationAad(credentialId: string, reservationId: string, gatewayId: string, credType: number): string {
  return [REPLICATION_LABEL, credentialId.toLowerCase(), reservationId.toLowerCase(), gatewayId.toLowerCase(), String(credType)].join(':');
}

/**
 * Seal `plaintext` to one recipient with a given sender secret and nonce. Every
 * input is explicit so the known-answer test can pin the output; production
 * callers go through {@link sealCredential}, which draws both at random.
 */
export function sealWith(
  plaintext: Uint8Array,
  aad: string,
  senderSecret: Uint8Array,
  recipientPublic: Uint8Array,
  nonce: Uint8Array,
): { sourcePublicKey: Uint8Array; ciphertext: Uint8Array } {
  if (recipientPublic.length !== 32) {
    throw new Error('A gateway replication key must be 32 bytes.');
  }
  if (nonce.length !== NONCE_LEN) {
    throw new Error(`The nonce must be ${String(NONCE_LEN)} bytes.`);
  }
  // noble refuses a low-order key itself; this keeps the rule visible here.
  const shared = x25519.getSharedSecret(senderSecret, recipientPublic);
  if (shared.every((b) => b === 0)) {
    throw new Error("The gateway's replication key is not usable.");
  }
  const key = hkdf(sha256, shared, undefined, new TextEncoder().encode(REPLICATION_LABEL), KEY_LEN);
  const sealed = xchacha20poly1305(key, nonce, new TextEncoder().encode(aad)).encrypt(plaintext);
  const ciphertext = new Uint8Array(NONCE_LEN + sealed.length);
  ciphertext.set(nonce, 0);
  ciphertext.set(sealed, NONCE_LEN);
  return { sourcePublicKey: x25519.getPublicKey(senderSecret), ciphertext };
}

/**
 * Seal a credential for every reserved target, each with its own fresh key.
 * Throws rather than seal something the gateway would read differently: a
 * reservation for another type, or a target with a malformed key.
 */
export function sealCredential(credential: SealableCredential, reservation: SealReservation): SealedCopy[] {
  const expected = PROTO_CREDENTIAL_TYPE[credential.type];
  if (reservation.credType !== expected) {
    throw new Error(`The reservation is for credential type ${String(reservation.credType)}, not ${credential.type}.`);
  }
  const plaintext = new TextEncoder().encode(materialJson(credential));
  return reservation.targets.map((target) => {
    const aad = replicationAad(reservation.credentialId, reservation.reservationId, target.gatewayId, reservation.credType);
    const { sourcePublicKey, ciphertext } = sealWith(
      plaintext,
      aad,
      x25519.utils.randomSecretKey(),
      fromBase64(target.recipientPublicKey),
      randomBytes(NONCE_LEN),
    );
    return { gatewayId: target.gatewayId, sourcePublicKey: toBase64(sourcePublicKey), ciphertext: toBase64(ciphertext) };
  });
}

/** Standard base64 with padding, which the backend decodes. */
export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

export function fromBase64(text: string): Uint8Array {
  const binary = atob(text);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
